import {RefObject, useId} from "react";
import {css, cx} from "@panda/css";

import {SearchRepeatIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";

import {IPersonalizedRecentSearch} from "../../types/IPersonalizedHomepage";

interface IProps {
    searches: IPersonalizedRecentSearch[];
    isLoading?: boolean;
    className?: string;
    maxItems?: number;
    sectionRef?: RefObject<HTMLElement | null>;
    onRecentSearchClick?: (search: IPersonalizedRecentSearch, index: number) => void;
}

const RECENT_SEARCHES_SKELETON_ITEMS = [0, 1, 2, 3];

export const PersonalizedHomepageRecentSearches = (props: IProps) => {
    const {searches, isLoading = false, className, maxItems = 4, sectionRef, onRecentSearchClick} = props;
    const headingId = useId();
    const visibleSearches = searches.slice(0, maxItems);

    if (!isLoading && visibleSearches.length === 0) {
        return null;
    }

    return (
        <section className={cx(recentSearchesStyle, className)} ref={sectionRef} aria-labelledby={headingId} aria-busy={isLoading}>
            <Text id={headingId} as="h2" variant="info_txt_2" className={recentSearchesHeadingStyle}>
                Ostatnio wyszukiwane:
            </Text>

            <ul className={recentSearchesListStyle}>
                {isLoading
                    ? RECENT_SEARCHES_SKELETON_ITEMS.map((item) => (
                          <li key={item} className={recentSearchesItemStyle}>
                              <span className={recentSearchSkeletonStyle} />
                          </li>
                      ))
                    : visibleSearches.map((search, index) => (
                          <li key={search.id} className={recentSearchesItemStyle}>
                              <a
                                  className={recentSearchStyle}
                                  href={search.href}
                                  title={`${search.title} — ${search.description}`}
                                  onClick={() => onRecentSearchClick?.(search, index)}
                              >
                                  <span className={recentSearchCopyStyle}>
                                      <span className={recentSearchTitleRowStyle}>
                                          <Text as="span" variant="body_copy_2" className={recentSearchTitleStyle}>
                                              {search.title}
                                          </Text>
                                          {search.newOffersLabel ? (
                                              <Text as="span" variant="info_txt_3" className={recentSearchNewPillStyle}>
                                                  {search.newOffersLabel}
                                              </Text>
                                          ) : null}
                                      </span>
                                      <Text as="span" variant="info_txt_2" className={recentSearchDescriptionStyle}>
                                          {search.description}
                                      </Text>
                                  </span>

                                  <span className={recentSearchIconStyle}>
                                      <SearchRepeatIcon size="2.4" fill="var(--colors-white)" />
                                  </span>
                              </a>
                          </li>
                      ))}
            </ul>
        </section>
    );
};

const recentSearchesStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 1,
    width: "100%"
});

const recentSearchesHeadingStyle = css({
    display: "none",
    margin: 0,
    color: "rgba(255, 255, 255, 0.8)",
    fontWeight: 400,

    md: {
        display: "block"
    }
});

const recentSearchesListStyle = css({
    display: "flex",
    gap: 1,
    margin: 0,
    padding: 0,
    listStyle: "none",
    overflowX: "auto",
    scrollbarWidth: "none",
    scrollSnapType: "x mandatory",

    "&::-webkit-scrollbar": {
        display: "none"
    },

    md: {
        overflowX: "visible"
    }
});

const recentSearchesItemStyle = css({
    flex: "0 0 auto",
    width: "min(32rem, 100%)",
    scrollSnapAlign: "start",

    md: {
        flex: "1 1 0",
        width: "auto",
        minWidth: 0
    }
});

const recentSearchStyle = css({
    display: "flex",
    alignItems: "center",
    gap: 2,
    boxSizing: "border-box",
    width: "100%",
    height: "auto",
    py: 1,
    pr: 1,
    pl: "1.2rem",
    color: "var(--colors-white)",
    backgroundColor: "rgba(35, 35, 45, 0.58)",
    borderRadius: "1.2rem",
    backdropFilter: "blur(4px)",
    textDecoration: "none",
    transition: "background-color 160ms ease",

    md: {
        height: "6.4rem",
        pt: 1.5,
        pr: 1.5,
        pb: 1.5,
        pl: 2
    },

    _hover: {
        color: "var(--colors-white)",
        backgroundColor: "rgba(35, 35, 45, 0.76)",
        textDecoration: "none"
    },

    _focusVisible: {
        outline: "2px solid var(--colors-primary)",
        outlineOffset: "2px"
    }
});

const recentSearchSkeletonStyle = css({
    display: "block",
    boxSizing: "border-box",
    width: "100%",
    height: "5.6rem",
    backgroundColor: "rgba(35, 35, 45, 0.4)",
    borderRadius: "1.2rem",
    backdropFilter: "blur(4px)",

    md: {
        height: "6.4rem"
    }
});

const recentSearchCopyStyle = css({
    display: "flex",
    flexDirection: "column",
    flex: "1 1 0",
    gap: 1,
    minWidth: 0
});

const recentSearchTitleRowStyle = css({
    display: "flex",
    alignItems: "center",
    gap: 0.5,
    minWidth: 0
});

const recentSearchTitleStyle = css({
    overflow: "hidden",
    color: "var(--colors-white)",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
});

const recentSearchNewPillStyle = css({
    flex: "0 0 auto",
    px: 1,
    py: 0.5,
    color: "var(--colors-secondary)",
    backgroundColor: "var(--colors-success)",
    borderRadius: "999px",
    whiteSpace: "nowrap"
});

const recentSearchDescriptionStyle = css({
    overflow: "hidden",
    color: "var(--colors-white)",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
});

const recentSearchIconStyle = css({
    display: "flex",
    flex: "0 0 auto",
    alignItems: "center",
    justifyContent: "center",
    padding: 1,
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    borderRadius: 1,
    backdropFilter: "blur(4px)"
});
import {RefObject, useId} from "react";
import {css, cx} from "@panda/css";

import {SearchRepeatIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";

import {IPersonalizedRecentSearch} from "../../types/IPersonalizedHomepage";

interface IProps {
    searches: IPersonalizedRecentSearch[];
    isLoading?: boolean;
    className?: string;
    maxItems?: number;
    sectionRef?: RefObject<HTMLElement | null>;
    onRecentSearchClick?: (search: IPersonalizedRecentSearch, index: number) => void;
}

const RECENT_SEARCHES_SKELETON_ITEMS = [0, 1, 2, 3];

export const PersonalizedHomepageRecentSearches = (props: IProps) => {
    const {searches, isLoading = false, className, maxItems = 4, sectionRef, onRecentSearchClick} = props;
    const headingId = useId();
    const visibleSearches = searches.slice(0, maxItems);

    if (!isLoading && visibleSearches.length === 0) {
        return null;
    }

    return (
        <section className={cx(recentSearchesStyle, className)} ref={sectionRef} aria-labelledby={headingId} aria-busy={isLoading}>
            <Text id={headingId} as="h2" variant="info_txt_2" className={recentSearchesHeadingStyle}>
                Ostatnio wyszukiwane:
            </Text>

            <ul className={recentSearchesListStyle}>
                {isLoading
                    ? RECENT_SEARCHES_SKELETON_ITEMS.map((item) => (
                          <li key={item} className={recentSearchesItemStyle}>
                              <span className={recentSearchSkeletonStyle} />
                          </li>
                      ))
                    : visibleSearches.map((search, index) => (
                          <li key={search.id} className={recentSearchesItemStyle}>
                              <a
                                  className={recentSearchStyle}
                                  href={search.href}
                                  title={`${search.title} — ${search.description}`}
                                  onClick={() => onRecentSearchClick?.(search, index)}
                              >
                                  <span className={recentSearchCopyStyle}>
                                      <span className={recentSearchTitleRowStyle}>
                                          <Text as="span" variant="body_copy_2" className={recentSearchTitleStyle}>
                                              {search.title}
                                          </Text>
                                          {search.newOffersLabel ? (
                                              <Text as="span" variant="info_txt_3" className={recentSearchNewPillStyle}>
                                                  {search.newOffersLabel}
                                              </Text>
                                          ) : null}
                                      </span>
                                      <Text as="span" variant="info_txt_2" className={recentSearchDescriptionStyle}>
                                          {search.description}
                                      </Text>
                                  </span>

                                  <span className={recentSearchIconStyle}>
                                      <SearchRepeatIcon size="2.4" fill="var(--colors-white)" />
                                  </span>
                              </a>
                          </li>
                      ))}
            </ul>
        </section>
    );
};

const recentSearchesStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 1,
    width: "100%"
});

const recentSearchesHeadingStyle = css({
    display: "none",
    margin: 0,
    color: "rgba(255, 255, 255, 0.8)",
    fontWeight: 400,

    md: {
        display: "block"
    }
});

const recentSearchesListStyle = css({
    display: "flex",
    gap: 1,
    margin: 0,
    padding: 0,
    listStyle: "none",
    overflowX: "auto",
    scrollbarWidth: "none",
    scrollSnapType: "x mandatory",

    "&::-webkit-scrollbar": {
        display: "none"
    },

    md: {
        overflowX: "visible"
    }
});

const recentSearchesItemStyle = css({
    flex: "0 0 auto",
    width: "min(32rem, 100%)",
    scrollSnapAlign: "start",

    md: {
        flex: "1 1 0",
        width: "auto",
        minWidth: 0
    }
});

const recentSearchStyle = css({
    display: "flex",
    alignItems: "center",
    gap: 2,
    boxSizing: "border-box",
    width: "100%",
    height: "auto",
    py: 1,
    pr: 1,
    pl: "1.2rem",
    color: "var(--colors-white)",
    backgroundColor: "rgba(35, 35, 45, 0.58)",
    borderRadius: "1.2rem",
    backdropFilter: "blur(4px)",
    textDecoration: "none",
    transition: "background-color 160ms ease",

    md: {
        height: "6.4rem",
        pt: 1.5,
        pr: 1.5,
        pb: 1.5,
        pl: 2
    },

    _hover: {
        color: "var(--colors-white)",
        backgroundColor: "rgba(35, 35, 45, 0.76)",
        textDecoration: "none"
    },

    _focusVisible: {
        outline: "2px solid var(--colors-primary)",
        outlineOffset: "2px"
    }
});

const recentSearchSkeletonStyle = css({
    display: "block",
    boxSizing: "border-box",
    width: "100%",
    height: "5.6rem",
    backgroundColor: "rgba(35, 35, 45, 0.4)",
    borderRadius: "1.2rem",
    backdropFilter: "blur(4px)",

    md: {
        height: "6.4rem"
    }
});

const recentSearchCopyStyle = css({
    display: "flex",
    flexDirection: "column",
    flex: "1 1 0",
    gap: 1,
    minWidth: 0
});

const recentSearchTitleRowStyle = css({
    display: "flex",
    alignItems: "center",
    gap: 0.5,
    minWidth: 0
});

const recentSearchTitleStyle = css({
    overflow: "hidden",
    color: "var(--colors-white)",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
});

const recentSearchNewPillStyle = css({
    flex: "0 0 auto",
    px: 1,
    py: 0.5,
    color: "var(--colors-secondary)",
    backgroundColor: "var(--colors-success)",
    borderRadius: "999px",
    whiteSpace: "nowrap"
});

const recentSearchDescriptionStyle = css({
    overflow: "hidden",
    color: "var(--colors-white)",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
});

const recentSearchIconStyle = css({
    display: "flex",
    flex: "0 0 auto",
    alignItems: "center",
    justifyContent: "center",
    padding: 1,
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    borderRadius: 1,
    backdropFilter: "blur(4px)"
});
